import { Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';

interface ExploreClassificationHeaderProps {
  /**
   * Classification name
   * @required
   */
  classificationName: string;
  /**
   * Classification description
   * @required
   */
  description: string;
  /**
   * Whether the component is in loading state
   * @optional
   * @default false
   */
  isLoading?: boolean;
  /**
   * Custom CSS class for the container
   * @optional
   */
  className?: string;
}

const containerClasses = cva({
  base: 'flex flex-col gap-6',
});

/**
 * ExploreClassificationHeader displays the classification name and description.
 * This is a presentational component used across explore pages.
 *
 * Features:
 * - Classification name with medium weight
 * - Description text with tertiary color
 * - Loading states with skeleton placeholders
 * - Responsive text layout
 *
 * @example
 * <ExploreClassificationHeader
 *   classificationName="Lifestyle"
 *   description="Images showing the product in real-world use..."
 *   isLoading={false}
 * />
 */
export const ExploreClassificationHeader = (props: ExploreClassificationHeaderProps) => {
  const { classificationName, description, isLoading = false, className, ...other } = props;
  return (
    <div className={cx(containerClasses(), className)} {...other}>
      {isLoading ? (
        <Skeleton variant="square" className="h-12 w-60" />
      ) : (
        <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Primary}>
          {classificationName}
        </Text>
      )}
      {isLoading ? (
        <Skeleton variant="square" className="h-12 w-[400px]" />
      ) : (
        <Text size={TextSize.Md} color={TextColor.Tertiary} maxLines={2}>
          {description}
        </Text>
      )}
    </div>
  );
};

export type { ExploreClassificationHeaderProps };
